import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import s from './s.module.less';
import { Button, Form, message, Modal, Spin } from 'antd';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { IServiceAddressUpdateInput, PracticeAddressUpdateType } from 'types/applicationForm';
import { getUserId } from 'utils/localstore';
import { IService } from 'types/practiceFront';
import { updateProviderServiceAddress, getAllServiceByPracticeAddressId } from 'api/applicationForm';
import { getAllServiceByPracticeAddressId as getOpsAllServiceByPracticeAddressId, updateProviderServiceAddress as updateOpsProviderServiceAddress } from 'api/operation';
import PracticeAddressRemovement from 'components/PracticeAddressRemovement';
import { useParams } from 'react-router-dom';
import { practiceAddressTypeMapping } from 'constants/uniprofile';
import { Address, AddressPublicType } from 'types/paymentSetup';
import useGetStateLabel from 'hooks/useGetStateLabel';
import AddressCard from '../AddressCard';

type IFormData = {
    addressList: Address[];
};

interface IProps {
    isOps?: boolean;
    formName: string;
    initFormValue: Address[];
    enable: boolean;
}

interface OptionType {
    value: string | number;
    label: React.ReactNode;
}

const PracticeAddress = forwardRef<any, IProps>(({
    isOps,
    initFormValue,
    formName,
    enable,
}, ref) => {
    const { pid } = useParams();
    const providerId = pid ? parseInt(pid, 10) : 0;
    const [setting] = useSettings();
    const addressTypeSetting = setting[EnumFields.ADDRESS_PUBLIC_TYPE];
    const { getStateLabel } = useGetStateLabel();

    const addressTypeOptions: OptionType[] = useMemo(() => {
        if (addressTypeSetting) {
            return addressTypeSetting.filter((e) => e.value !== undefined).map((e) => {
                const { value } = e;
                const typeMapping = practiceAddressTypeMapping[value! as keyof typeof practiceAddressTypeMapping];
                return { value: value!, label: typeMapping || e.label };
            });
        }
        return [];
    }, [addressTypeSetting]);
    const [practiceAddressForm] = Form.useForm<IFormData>();
    const originFormValues = useRef<Partial<IFormData>>();
    const [addressEditType, setAddressEditType] = useState<PracticeAddressUpdateType>(PracticeAddressUpdateType.UPDATE);
    const [defaultAddressIndex, setDefaultAddressIndex] = useState<number | null>(0);
    const [showAddressUpdateModal, setShowAddressUpdateModal] = useState(false);
    const [fetchServiceLoading, setFetchServiceLoading] = useState<boolean>(false);
    const [serviceListData, setServiceListData] = useState<Array<IService>>([]);
    const [updateServiceAddressData, setUpdateServiceAddressData] = useState<Array<IServiceAddressUpdateInput>>([]);
    const formListRemoveFn = useRef<null | ((index: number | number[]) => void)>(null);

    const [updatedAddressIdList, setUpdatedAddressIdList] = useState<Array<number>>([]);

    useEffect(() => {
        const initialList: Address[] = initFormValue?.length ? [...initFormValue] : [{ isDefault: true } as Address];
        originFormValues.current = { addressList: [...initialList] };
        practiceAddressForm.setFieldsValue({ addressList: initialList });
        const defaultIndex = initialList.findIndex((addr) => addr.isDefault);
        setDefaultAddressIndex(defaultIndex >= 0 ? defaultIndex : 0);
    }, [initFormValue, practiceAddressForm]);

    const fetchServiceList = useCallback(async (providerID: number, addressId: number): Promise<{ data?: { data: IService[] }, error?: string }> => {
        setFetchServiceLoading(true);
        let res: { data?: { data: IService[] }, error?: string } = {};
        try {
            const fetchFunc = isOps ? getOpsAllServiceByPracticeAddressId : getAllServiceByPracticeAddressId;
            res = await fetchFunc(providerID, addressId);
        } catch (e: unknown) {
            res.error = e instanceof Error ? e.message : String(e);
        }
        setFetchServiceLoading(false);
        return res;
    }, [isOps]);

    const getFormValues = useCallback(async () => {
        try {
            await practiceAddressForm.validateFields();
            const values = practiceAddressForm.getFieldsValue();
            const addressList = values.addressList?.map((addr, index) => ({
                ...addr,
                isDefault: index === defaultAddressIndex,
            })) ?? [];
            return { ...values, addressList };
        } catch (e: unknown) {
            console.error('Validation Failed:', e);
            throw e;
        }
    }, [practiceAddressForm, defaultAddressIndex]);

    useImperativeHandle(ref, () => ({
        getFormValues,
    }));

    const handleDefaultAddressChange = useCallback((newIndex: number) => {
        setDefaultAddressIndex(newIndex);
        const currentValues = practiceAddressForm.getFieldsValue();
        const updatedList = currentValues.addressList.map((addr, index) => ({ ...addr, isDefault: index === newIndex }));
        practiceAddressForm.setFieldsValue({ addressList: updatedList });
    }, [practiceAddressForm]);

    const checkAddable = useCallback((add: (defaultValue?: Partial<Address>) => void) => {
        practiceAddressForm.validateFields().then(() => {
            add({ isDefault: false } as Partial<Address>);
        }).catch(() => {
            message.error('Please fill in all required fields for existing addresses first.');
        });
    }, [practiceAddressForm]);

    const handleBeforeRemove = useCallback(async (addressId: number, removeCallback: () => void) => {
        const userId = isOps ? providerId : getUserId();
        if (!userId) {
            message.error('User ID not found');
            return;
        }
        const res = await fetchServiceList(userId, addressId);
        if (!res.error && res.data?.data?.length) {
            setServiceListData(res.data.data);
            setAddressEditType(PracticeAddressUpdateType.REMOVE);
            setShowAddressUpdateModal(true);
            formListRemoveFn.current = removeCallback;
        } else if (!res.error) {
            removeCallback();
        } else {
            message.error(res.error || 'Failed to fetch associated services');
        }
    }, [fetchServiceList, isOps, providerId]);

    const handleRemove = useCallback((index: number, remove: (index: number | number[]) => void) => {
        const values = practiceAddressForm.getFieldsValue();
        const addressList = values.addressList || [];
        const removedItem = addressList[index];

        const removeAction = () => {
            remove(index);
            const remainingAddresses = practiceAddressForm.getFieldValue('addressList');
            if (index === defaultAddressIndex || remainingAddresses.length === 1) {
                const newDefaultIndex = remainingAddresses.length > 0 ? 0 : null;
                setDefaultAddressIndex(newDefaultIndex);
                if (newDefaultIndex !== null) {
                    const updatedList = remainingAddresses.map((addr: Address, i: number) => ({ ...addr, isDefault: i === newDefaultIndex }));
                    practiceAddressForm.setFieldsValue({ addressList: updatedList });
                }
            }
        };

        if (removedItem?.id) {
            handleBeforeRemove(removedItem.id, removeAction);
        } else {
            removeAction();
        }
    }, [handleBeforeRemove, practiceAddressForm, defaultAddressIndex]);

    const handleServiceAddressChange = useCallback(async (data: Array<IServiceAddressUpdateInput>) => {
        setUpdateServiceAddressData(data);
    }, []);

    const handleAddressChange = useCallback((index: number) => {
        const values = practiceAddressForm.getFieldsValue();
        const editItem = values.addressList?.[index];
        if (editItem?.id && !updatedAddressIdList.includes(editItem.id)) {
            setUpdatedAddressIdList((prev) => [...prev, editItem.id!]);
        }
    }, [practiceAddressForm, updatedAddressIdList]);

    const handleConfirm = useCallback(async () => {
        if (typeof formListRemoveFn.current === 'function') {
            try {
                (formListRemoveFn.current as () => void)();
            } catch (e) {
                console.error('Error calling remove function:', e);
            } finally {
                formListRemoveFn.current = null;
            }
        }
        let res: { error?: string } | undefined;
        try {
            const updateFunc = isOps ? updateOpsProviderServiceAddress : updateProviderServiceAddress;
            res = await updateFunc(updateServiceAddressData);
            if (res && !res.error) {
                setUpdatedAddressIdList([]);
                setShowAddressUpdateModal(false);
            } else {
                message.error(res?.error || 'Failed to update service addresses');
            }
        } catch (e: unknown) {
            message.error(e instanceof Error ? e.message : String(e));
        }
    }, [isOps, updateServiceAddressData]);

    const resetAddressVars = useCallback(() => {
        setUpdateServiceAddressData([]);
        formListRemoveFn.current = null;
    }, []);

    const handleHideAddressUpdateModal = useCallback(() => {
        setShowAddressUpdateModal(false);
        resetAddressVars();
    }, [resetAddressVars]);

    const editFormList = useMemo(() => (
        <Form.List name="addressList">
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }, index) => (
                        <div key={key} className={s.addressCardContainer}>
                            <div className={s.labelBox}>
                                <div className={s.label}>{`Practice address ${index + 1}`}</div>
                            </div>
                            <AddressCard
                                name={name}
                                restField={restField}
                                addressTypeOptions={addressTypeOptions}
                                handleAddressChange={handleAddressChange}
                                isSelectedDefault={index === defaultAddressIndex}
                                onSelectDefault={handleDefaultAddressChange}
                            />
                            {fields.length > 1 && <Button onClick={() => handleRemove(index, remove)}>Remove</Button>}
                        </div>
                    ))}
                    <Button type="text" onClick={() => checkAddable(add)} className={s.addAddressButton}>
                        + Add another practice address
                    </Button>
                </>
            )}
        </Form.List>
    ), [addressTypeOptions, checkAddable, handleAddressChange, handleRemove, defaultAddressIndex, handleDefaultAddressChange]);

    const renderViewList = useMemo(() => {
        const addressList = initFormValue || [];
        return addressList.map((item, index) => {
            const { practiceCity, practiceZip, practiceState, practiceAddress, isDefault, asMailing, publicType } = item;
            const addressLine1 = practiceAddress || '';
            const addressLine2 = `${practiceCity || ''}, ${getStateLabel(practiceState || '') || ''} ${practiceZip || ''}`.trim();

            return (
                <div key={index} className={s.addressViewCard}>
                    <div className={s.labelRow}>
                        <div className={s.label}>Practice address {index + 1}</div>
                        <div className={s.tagsContainer}>
                            {isDefault && <div className={s.defaultTag}>Default</div>}
                            {asMailing && <div className={s.mailingTag}>Mailing address</div>}
                            {publicType === AddressPublicType.PRIVATE && <div className={s.hiddenTag}>Hide to the Public</div>}
                        </div>
                    </div>
                    <div className={s.addressDetail}>
                        <div>{addressLine1}</div>
                        <div>{addressLine2}</div>
                    </div>
                </div>
            );
        });
    }, [initFormValue]);

    return (
        <div>
            <Spin spinning={fetchServiceLoading}>
                <Modal
                    title={`${addressEditType === PracticeAddressUpdateType.REMOVE ? 'Remove the address' : 'Update the address'}`}
                    centered
                    open={showAddressUpdateModal}
                    okText={`${addressEditType === PracticeAddressUpdateType.REMOVE ? 'Remove' : 'Update'}`}
                    onOk={handleConfirm}
                    onCancel={handleHideAddressUpdateModal}
                    width="50%"
                >
                    <div className={s.ServiceModalContent}>
                        <PracticeAddressRemovement
                            isOps={isOps}
                            providerId={providerId}
                            serviceList={serviceListData}
                            onChange={handleServiceAddressChange}
                            type={addressEditType}
                        />
                    </div>
                </Modal>

                {!enable ? (
                    <div>{renderViewList}</div>
                ) : (
                    <Form
                        scrollToFirstError
                        form={practiceAddressForm}
                        name={formName}
                        initialValues={{ addressList: originFormValues.current?.addressList || [] }}
                        autoComplete="off"
                        layout="vertical"
                    >
                        {editFormList}
                    </Form>
                )}

            </Spin>
        </div>
    );
});

export default PracticeAddress;
